import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { cn } from "@/shared/lib/utils";
import type { Param } from "../developer-api";

export function ParamInput({
  param,
  value,
  onChange,
}: {
  param: Param;
  value: string;
  onChange: (next: string) => void;
}) {
  if (param.type === "enum" && param.options) {
    return (
      <Select
        value={value || "__unset__"}
        onValueChange={(v) => onChange(v === "__unset__" ? "" : v)}
      >
        <SelectTrigger className="font-mono text-sm h-9">
          <SelectValue placeholder="(not set)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__unset__">(not set)</SelectItem>
          {param.options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const placeholder =
    param.type === "uuid"
      ? "00000000-0000-4000-8000-000000000000"
      : param.in === "query"
        ? "(not set)"
        : "";

  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="font-mono text-sm h-9"
    />
  );
}

export function ParamSection({
  title,
  children,
  border,
}: {
  title: string;
  children: React.ReactNode;
  border?: boolean;
}) {
  return (
    <div className={cn(border && "border-t border-border")}>
      <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/20 border-b border-border">
        {title}
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

export function ParamRow({
  param,
  value,
  onChange,
  error,
}: {
  param: Param;
  value: string;
  onChange: (next: string) => void;
  error: boolean;
}) {
  return (
    <div className="grid md:grid-cols-[200px_1fr] gap-4 px-4 py-3">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="font-mono text-sm">{param.name}</code>
          {param.required && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40">
              required
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {param.description}
        </div>
      </div>
      <div className={cn(error && "ring-1 ring-red-500/50 rounded-md")}>
        <ParamInput param={param} value={value} onChange={onChange} />
      </div>
    </div>
  );
}
